/**
 * Sentry Error Tracking Configuration
 *
 * Provides error tracking and performance monitoring for the React app.
 * Integrates with Sentry.io for real-time error reporting.
 */

import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry error tracking
 * Should be called as early as possible in the app lifecycle
 */
export const initSentry = () => {
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  const environment = process.env.REACT_APP_SENTRY_ENVIRONMENT || process.env.NODE_ENV;
  const release = process.env.REACT_APP_SENTRY_RELEASE || 'lickly-frontend@1.0.0';

  // Only initialize if DSN is provided
  if (!dsn) {
    console.log('Sentry DSN not configured, error tracking disabled');
    return false;
  }

  try {
    Sentry.init({
      dsn,
      environment,
      release,

      // Send default PII (IP address, etc.) - useful for debugging
      sendDefaultPii: true,

      // Performance Monitoring
      tracesSampleRate: parseFloat(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || '0.1'),

      // Session Replay (optional - for debugging)
      replaysSessionSampleRate: 0.0, // Disabled by default
      replaysOnErrorSampleRate: 0.1, // Record sessions on error

      // Integrations
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],

      // Don't send errors from development or specific files
      beforeSend(event, hint) {
        // Filter out userflow.js errors
        if (event.exception?.values?.[0]?.value?.includes('Userflow')) {
          return null;
        }

        // Filter out TrackJS errors
        if (event.exception?.values?.[0]?.value?.includes('TrackJS')) {
          return null;
        }

        // Filter out benign network errors
        if (event.exception?.values?.[0]?.type === 'ChunkLoadError') {
          return null;
        }

        return event;
      },

      // Don't log breadcrumbs for these URLs
      beforeBreadcrumb(breadcrumb) {
        // Filter out analytics/tracking requests from breadcrumbs
        if (breadcrumb.category === 'xhr' || breadcrumb.category === 'fetch') {
          const url = breadcrumb.data?.url || '';
          if (
            url.includes('analytics') ||
            url.includes('mixpanel') ||
            url.includes('userflow') ||
            url.includes('hubspot')
          ) {
            return null;
          }
        }
        return breadcrumb;
      },

      // Ignore errors from third-party scripts
      ignoreErrors: [
        // Browser extensions
        'top.GLOBALS',
        'originalCreateNotification',
        'canvas.contentDocument',
        'MyApp_RemoveAllHighlights',
        'http://tt.telewebion',
        'atomicFindClose',
        // Facebook/Meta
        'fb_xd_fragment',
        // Third-party tracking
        /Can't find variable: Userflow/,
        /TrackJS/,
        // Network errors
        'Network request failed',
        'Failed to fetch',
        'Load failed',
        'NetworkError',
        // Chrome extensions
        /Extensions/i,
        // Safari webkit
        /webkit-masked-url/,
      ],

      // Only report errors from our own code
      denyUrls: [
        // Chrome extensions
        /extensions\//i,
        /^chrome:\/\//i,
        /^chrome-extension:\/\//i,
        // Firefox extensions
        /^moz-extension:\/\//i,
        // Safari extensions
        /^safari-extension:\/\//i,
        // Other tracking scripts
        /analytics/i,
        /mixpanel/i,
        /hubspot/i,
        /userflow/i,
      ],
    });

    console.log('Sentry initialized', { environment, release });
    return true;
  } catch (error) {
    console.error('Failed to initialize Sentry:', error);
    return false;
  }
};

/**
 * Capture an exception and send to Sentry
 * @param {Error} error - The error to capture
 * @param {Object} context - Additional context to attach
 */
export const captureException = (error, context = {}) => {
  if (process.env.REACT_APP_SENTRY_DSN) {
    Sentry.withScope((scope) => {
      Object.entries(context).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
      Sentry.captureException(error);
    });
  }
};

/**
 * Capture a message and send to Sentry
 * @param {string} message - The message to capture
 * @param {string} level - Log level (info, warning, error)
 * @param {Object} context - Additional context to attach
 */
export const captureMessage = (message, level = 'info', context = {}) => {
  if (process.env.REACT_APP_SENTRY_DSN) {
    Sentry.withScope((scope) => {
      scope.setLevel(level);
      Object.entries(context).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
      Sentry.captureMessage(message);
    });
  }
};

/**
 * Set user context for Sentry events
 * @param {Object} user - User information { id, email, username }
 */
export const setUser = (user) => {
  if (process.env.REACT_APP_SENTRY_DSN && user) {
    Sentry.setUser({
      id: user.id || user.userId,
      email: user.email,
      username: user.username || user.name,
    });
  }
};

/**
 * Clear user context (on logout)
 */
export const clearUser = () => {
  Sentry.setUser(null);
};

/**
 * Add a breadcrumb for debugging
 * @param {Object} breadcrumb - { message, category, level, data }
 */
export const addBreadcrumb = (breadcrumb) => {
  Sentry.addBreadcrumb(breadcrumb);
};

/**
 * Set a tag for Sentry events
 * @param {string} key - Tag key
 * @param {string} value - Tag value
 */
export const setTag = (key, value) => {
  Sentry.setTag(key, value);
};

// Export Sentry's ErrorBoundary for use in components
export const SentryErrorBoundary = Sentry.ErrorBoundary;

export default Sentry;
